import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { OrderEditorDrawer } from "../components/orders/OrderEditorDrawer";
import { OrderDetailDrawer } from "../components/orders/OrderDetailDrawer";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, Sheet } from "../components/mobile/primitives";
import { SearchSelect, type SSOption } from "../components/ui/SearchSelect";
import { DateField } from "../components/ui/DateField";
import { HIZLI_ARALIKLAR, hizliAralik, type HizliAralik } from "../lib/trDate";
import { useDebounced } from "../lib/useDebounced";
// Durum etiketleri/rozetleri + "Açık siparişler" kısayolu + süzgeç girdisi TEK kaynaktan
// (MRP seçicisi ve Teslimat Planı da aynı modülü okur).
import {
  type SiparisDurumu, SIPARIS_DURUM_SIRASI, SIPARIS_DURUM_ETIKET, DURUM_SECENEKLERI,
  durumEtiketi, durumRozetSinifi, durumRozetSinifiMobil, durumlariSirala, durumSecimOzeti, durumSuzgecGirdisi,
  ACIK_SIPARISLER_AD, ACIK_SIPARISLER_KUME, acikSiparislerSecili, acikSiparislerKisayolu,
  turSecenekleri, turSecimOzeti, turSuzgecGirdisi, suzgeciUrlyeYaz,
} from "../lib/siparisDurum";
import "./Items.css";
import "./Orders.css";

interface OrderItem {
  code: string;
  name: string;
  qty: number;
  uom: string;
}

interface OrderRow {
  id: string;
  code: string;
  customerName: string;
  orderType: "customer" | "stock_replenish";
  status: SiparisDurumu;
  requestedDate: string | null;
  /** Etkin teslim tarihi — söz verilen, yoksa istenen (uçta tek yerde türetilir). */
  teslimTarihi: string | null;
  /** true = söz verilen tarih yok, istenen tarihe düşüldü (ekranda İŞARETLENİR). */
  teslimTahmini: boolean;
  /**
   * true = etkin teslim tarihi GEÇTİ ve sipariş hâlâ açık → satır koyu kırmızı çerçevelenir.
   * Kararı SUNUCU verir (`order-date.ts`): uçta hesaplansaydı "bugün" tarayıcı saat dilimine
   * göre kayar, KPI'daki geciken sayısı ile boyalı satır sayısı tutmazdı.
   */
  gecikti: boolean;
  createdAt: string;
  items: OrderItem[];
}

/** `order.filterSummary` çıktısı — süzgeçlenmiş TÜM siparişlerin üretim/planlama özeti. */
interface OzetVeri {
  siparisSayisi: number;
  gecikenSayisi: number;
  urunCesidi: number;
  /** Koli / kg / adet AYRI satırlar — tek sayıya İNDİRGENMEZ (bkz. CLAUDE.md). */
  birimToplam: Array<{ uom: string; qty: number; urunSayisi: number }>;
  urunler: Array<{ itemId: string; code: string; name: string; uom: string; qty: number; satirSayisi: number }>;
  durumlar: Array<{ status: OrderRow["status"]; adet: number }>;
}

/**
 * SÜZGEÇ ÖZETİ — süzgeç etkinken listenin ÜSTÜNDE duran KPI kartları + ürün kırılımı.
 *
 * Kullanıcı isteği (29 Ağu 2026): "filtrelediğimizde listede yer alan siparişlerin toplam
 * koli sayısı, sipariş adedi, ürünlere göre toplam kırılım… bu kartlar sadece filtre aktif
 * iken görünsün."
 *
 * ⚠ SAYFA DEĞİL, KÜME: rakamlar `order.filterSummary`den gelir ve süzgece uyan TÜM
 * siparişleri kapsar — ekrandaki 20 satırın toplamı değildir.
 * ⚠ BİRİM KARIŞTIRMA YOK: koli/kg/adet ayrı kutularda; tek bir "toplam" rakamı YAZILMAZ.
 * ⚠ SESSİZLİK YASAĞI: özet yüklenemezse kartlar sessizce gizlenmez, hata SÖYLENİR —
 * aksi halde kullanıcı "filtrede özet çıkmıyor" diye özelliği yok sanardı.
 */
function SuzgecOzeti({ veri, yukleniyor, hata, mobil, turSuzgeci }: { veri?: OzetVeri; yukleniyor: boolean; hata?: string; mobil: boolean; turSuzgeci: boolean }) {
  if (hata) {
    return (
      <div className={`ord-kpi-msg err${mobil ? " m" : ""}`} role="alert">
        <Icon name="alertTriangle" size={13} />Süzgeç özeti hesaplanamadı: {hata}
      </div>
    );
  }
  if (yukleniyor || !veri) return <div className={`ord-kpi-msg${mobil ? " m" : ""}`}>Süzgeç özeti hesaplanıyor…</div>;
  // Sonuç boşken kart göstermek anlamsız; boş liste durumu zaten ayrıca anlatılıyor.
  if (veri.siparisSayisi === 0) return null;

  const durumMetni = veri.durumlar.map((x) => `${fmtQty(x.adet)} ${durumEtiketi(x.status)}`).join(" · ");
  /**
   * "EN ÇOK" YALNIZ TEK BİRİM VARKEN SÖYLENİR. Küme hem koli hem kg içeriyorsa 1.200 kg ile
   * 500 koli kıyaslanamaz; birimsiz bir üstünlük iddiası planlamacıyı yanlış ürüne yönlendirir.
   * Çok birimli kümede bunun yerine hangi birimlerin karıştığı yazılır.
   */
  const tekBirim = veri.birimToplam.length === 1;
  const enCok = tekBirim ? veri.urunler[0] : undefined;
  const gecikmeVar = veri.gecikenSayisi > 0;

  return (
    <div className={`ord-kpi${mobil ? " m" : ""}`}>
      <div className="ord-kpi-row">
        <div className="dash-kpi" style={{ ["--kpi-color" as string]: "var(--primary)" }}>
          <div className="head"><span className="lbl">Sipariş</span></div>
          <div className="val">{fmtQty(veri.siparisSayisi)}<span className="unit">sipariş</span></div>
          <div className="sub" title={durumMetni}>{durumMetni || "—"}</div>
        </div>

        <div className="dash-kpi" style={{ ["--kpi-color" as string]: "var(--info)" }}>
          <div className="head"><span className="lbl">Ürün Çeşidi</span></div>
          <div className="val">{fmtQty(veri.urunCesidi)}<span className="unit">ürün</span></div>
          <div className="sub" title={enCok ? `${enCok.name} — ${fmtQty(enCok.qty)} ${enCok.uom}` : undefined}>
            {enCok
              ? `En çok: ${enCok.name} · ${fmtQty(enCok.qty)} ${enCok.uom}`
              : `${fmtQty(veri.birimToplam.length)} farklı birim: ${veri.birimToplam.map((b) => b.uom).join(" · ")}`}
          </div>
        </div>

        {/* Durum = renk + İKON + METİN (CLAUDE.md kuralı); renk tek başına yetmez. */}
        <div className="dash-kpi" style={{ ["--kpi-color" as string]: gecikmeVar ? "var(--danger-text)" : "var(--success)" }}>
          <div className="head"><span className="lbl">Geciken</span></div>
          <div className="val" style={gecikmeVar ? { color: "var(--danger-text)" } : undefined}>
            {fmtQty(veri.gecikenSayisi)}<span className="unit">sipariş</span>
          </div>
          <div className="sub">
            <Icon name={gecikmeVar ? "alertTriangle" : "checkCircle"} size={11} />
            {gecikmeVar ? " teslim tarihi geçti" : " gecikme yok"}
          </div>
        </div>
      </div>

      {/* TOPLAM MİKTAR tek kart, birimler yan yana — teslimat planındaki tutar kartıyla aynı desen. */}
      <div className="dash-kpi ord-kpi-qty" style={{ ["--kpi-color" as string]: "var(--success)" }}>
        <div className="head">
          <span className="lbl">Toplam Miktar</span>
          {/* Ürün türü süzgeci varken toplam yalnız o türdeki SATIRLARI sayar (sunucuda
              `satirKosulu`) — karışık bir siparişin öteki türdeki satırı buraya girmez; SÖYLENİR. */}
          <span className="ord-kpi-note">{turSuzgeci ? "yalnız seçili ürün türü satırları · " : ""}birimler ayrı toplanır</span>
        </div>
        <div className="ord-qty-list">
          {veri.birimToplam.map((b) => (
            <div key={b.uom} className="ord-qty-item">
              <span className="q mono">{fmtQty(b.qty)}</span>
              <span className="u">{b.uom}</span>
              <span className="d">{fmtQty(b.urunSayisi)} ürün</span>
            </div>
          ))}
        </div>
      </div>

      {/* ÜRÜN KIRILIMI — üretim planlaması bunun üzerinden yapılıyor; kırpılmaz, kaydırılır. */}
      {/* KATLANABİLİR: blok tablonun dikey payını yiyor; kullanıcı bu dengeyi kendi kursun.
          `<details>` yerlisi klavye ve ekran okuyucuyla zaten çalışır — özel bir açılır yazılmadı. */}
      <details className="ord-urun" open>
        <summary className="ord-urun-head">
          <Icon name="package" size={13} />Ürün Kırılımı
          <span className="ct">{fmtQty(veri.urunler.length)} satır</span>
        </summary>
        <div className="ord-urun-scroll">
          <table className="ord-urun-tbl">
            <thead>
              <tr>
                <th>Ürün</th>
                <th className="c">Kod</th>
                <th className="r">Miktar</th>
                <th>Birim</th>
                <th className="r">Sipariş Satırı</th>
              </tr>
            </thead>
            <tbody>
              {veri.urunler.map((u) => (
                <tr key={`${u.itemId}|${u.uom}`}>
                  <td title={u.name}>{u.name}</td>
                  <td className="mono c">{u.code}</td>
                  <td className="mono r"><b>{fmtQty(u.qty)}</b></td>
                  <td className="u">{u.uom}</td>
                  <td className="mono r">{fmtQty(u.satirSayisi)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

export function OrdersPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  // ── Filtreler ──
  /**
   * ÇOK SEÇİMLİ durum (Maya Food, 14 Eki 2026: "taslak, onaylı ve üretimdeki siparişleri toplu
   * göremiyoruz"). Boş = Tümü (varsayılan). Seçim her zaman yaşam döngüsü sırasında tutulur →
   * aynı küme farklı tıklama sırasıyla seçildiğinde sorgu anahtarı değişmez.
   */
  const [statuses, setStatuses] = useState<SiparisDurumu[]>([]);
  /** ÜRÜN TÜRÜ (kart türü: Noodle / Tortilla …) — kimlikler + "Tür atanmamış" değeri (siparisDurum.ts). */
  const [turler, setTurler] = useState<string[]>([]);
  /** Mobil süzgeç çekmecesi. */
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  /**
   * Seçili hızlı aralık. Kullanıcı tarihleri ELLE değiştirirse boşalır — aksi halde
   * "Bu ay" yazan bir rozet, artık bu aya karşılık gelmeyen bir aralığı anlatırdı.
   */
  const [hizli, setHizli] = useState<HizliAralik | "">("");
  const pageSize = 20;
  const navigate = useNavigate();
  /**
   * YENİ SİPARİŞ artık ÇEKMECE DEĞİL, uygulama içi SEKME.
   * Uzun form (çok satır + fiyat anlaşması + ATP) doldurulurken kullanıcı stoğa/reçeteye
   * bakıp geri dönebilsin; çekmecede sayfadan çıkmak girişi siliyordu.
   */
  const yeniSiparis = () => navigate("/orders/yeni");

  /**
   * TESLİMAT PLANI — o anki SÜZGEÇ SETİNİ (seçili sayfayı değil) yeni sekmedeki A4 rapora taşır.
   * Rapor ekranı bu değerleri yalnız başlangıç olarak alır; kullanıcı orada değiştirebilir.
   * Popup engellenirse SESSİZ kalmaz — kullanıcıya söylenir.
   */
  const [planHata, setPlanHata] = useState<string | null>(null);
  const teslimatPlani = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    // Durum `statuses=a,b`, tür `itemTypeIds=…&turAtanmamis=1` — rapor aynı yardımcıyla okur.
    suzgeciUrlyeYaz(p, statuses, turler);
    if (customerIds.length) p.set("customerIds", customerIds.join(","));
    if (dateFrom) p.set("dateFrom", dateFrom);
    if (dateTo) p.set("dateTo", dateTo);
    // ⚠ `window.open(url, "_blank", "noopener")` SPEC GEREĞİ HER ZAMAN null döner → uyarı
    // rapor başarıyla açılsa bile basılıyordu. noopener'ı özellik dizesiyle değil, açılan
    // pencerenin `opener` bağını koparıp sağlıyoruz; böylece null GERÇEKTEN "engellendi" demek.
    const w = window.open(`/print/teslimat-plani${p.toString() ? `?${p}` : ""}`, "_blank");
    if (w) w.opener = null;
    setPlanHata(w ? null : "Teslimat planı açılamadı — tarayıcı açılır pencereyi engelledi. Bu site için izin verin.");
  };
  const [editorOpen, setEditorOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  /**
   * SÜZGEÇ TEK YERDE KURULUR ve hem listeye hem özete AYNI nesne olarak gider (ayna kuralı
   * uçta da geçerli): ikisi ayrı ayrı yazılsaydı bir süzgeç eklenip birine unutulunca kart,
   * listede görünmeyen siparişleri sayardı.
   */
  /**
   * Arama GECİKMELİ okunur: her tuş vuruşu yeni bir sorgu anahtarı üretiyordu ve özet uç
   * başına birkaç toplama sorgusu çalıştırdığı için "tortilla" yazmak 8 tur istek doğuruyordu
   * (KPI bloğu da her turda yükleniyor durumuna düşüp yerleşimi zıplatıyordu).
   * Gecikmeli değer HEM listeye HEM özete gider — biri anlık biri gecikmeli okusaydı ikisi
   * geçici olarak FARKLI soruları yanıtlardı.
   */
  const qGecikmeli = useDebounced(q, 300);
  const filtre = useMemo(() => ({
    q: qGecikmeli.trim() || undefined,
    ...durumSuzgecGirdisi(statuses),
    ...turSuzgecGirdisi(turler),
    customerIds: customerIds.length ? customerIds : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [qGecikmeli, statuses, turler, customerIds, dateFrom, dateTo]);
  const input = useMemo(() => ({ page, pageSize, ...filtre }), [page, filtre]);
  const ordersQ = trpc.order.list.useQuery(input);

  /**
   * Müşteri süzgeci seçenekleri.
   *
   * ⚠ `pageSize` ÜST SINIRI 100 (`ListInput`, routers/_schemas.ts:16). 200 istendiğinde
   * şema isteği reddediyor, sorgu hataya düşüyor ve ekranda "Müşteri bulunamadı" yazıyordu —
   * yani BOŞ LİSTE ile HATA aynı görünüyordu. Sınıra uyuyoruz ve hatayı ayrıca söylüyoruz.
   */
  const customersQ = trpc.customer.list.useQuery({ page: 1, pageSize: 100 });
  const customerOptions = useMemo<SSOption[]>(
    () => (customersQ.data?.rows ?? []).map((c) => ({ value: c.id, label: c.name, desc: c.code })),
    [customersQ.data],
  );
  /** SESSİZ KIRPMA YOK: 100'den fazla müşteri varsa liste eksiktir, söylüyoruz. */
  const musteriEksik = (customersQ.data?.total ?? 0) > customerOptions.length;
  const musteriBosMetni = customersQ.isLoading
    ? "Yükleniyor…"
    : customersQ.isError
      ? `Müşteriler yüklenemedi: ${customersQ.error.message}`
      : "Müşteri bulunamadı";
  /**
   * ÜRÜN TÜRÜ seçenekleri — Ayarlar > Kart Türleri'nin MAMUL türleri (sipariş satırı yalnız mamul).
   * "Noodle/Tortilla" KODLANMAZ: tür eklenince/yeniden adlandırılınca süzgeç kendiliğinden güncellenir.
   */
  const typesQ = trpc.settings.itemType.list.useQuery({ kind: "finished" }, { staleTime: 60_000 });
  const turListesi = useMemo(
    () => (typesQ.data ?? []).map((t) => ({ id: t.id, code: t.code, name: t.name })),
    [typesQ.data],
  );
  const turOptions = useMemo<SSOption[]>(() => turSecenekleri(turListesi), [turListesi]);
  const turBosMetni = typesQ.isLoading
    ? "Yükleniyor…"
    : typesQ.isError
      ? `Ürün türleri yüklenemedi: ${typesQ.error.message}`
      : "Tür bulunamadı";
  const acikSecili = acikSiparislerSecili(statuses);
  function durumSec(v: string[]) { setStatuses(durumlariSirala(v)); setPage(1); }
  /** Kısayol aç/kapa: seçim zaten "Açık siparişler"se Tümü'ne döner. */
  function acikKisayol() { setStatuses(acikSiparislerKisayolu(statuses)); setPage(1); }
  function turSec(v: string[]) { setTurler(v); setPage(1); }
  function durumAcKapa(s: SiparisDurumu) {
    durumSec(statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s]);
  }
  function turAcKapa(v: string) {
    turSec(turler.includes(v) ? turler.filter((x) => x !== v) : [...turler, v]);
  }
  const hizliOptions = useMemo<SSOption[]>(
    () => HIZLI_ARALIKLAR.map((h) => ({ value: h.key, label: h.label })),
    [],
  );

  /** Hazır aralık seçimi tarihleri DOLDURUR — kullanıcı sonra tek tek düzeltebilir. */
  function hizliSec(key: HizliAralik | null) {
    setPage(1);
    if (!key) { setHizli(""); setDateFrom(""); setDateTo(""); return; }
    const r = hizliAralik(key);
    setHizli(key); setDateFrom(r.from); setDateTo(r.to);
  }
  /** Elle tarih değişikliği hazır aralık rozetini düşürür (artık o dönemi anlatmıyor). */
  function tarihElle(hangi: "from" | "to", v: string) {
    setPage(1); setHizli("");
    if (hangi === "from") setDateFrom(v); else setDateTo(v);
  }

  const filtreSayisi = [statuses.length ? "1" : "", turler.length ? "1" : "", customerIds.length ? "1" : "", dateFrom, dateTo].filter(Boolean).length;
  function filtreleriTemizle() {
    setStatuses([]); setTurler([]); setCustomerIds([]); setDateFrom(""); setDateTo(""); setHizli(""); setPage(1);
  }
  /**
   * Süzgeç ETKİN Mİ? Kartların görünme koşulu (kullanıcı isteği: "sadece filtre aktif iken").
   * Arama da bir süzgeçtir — `filtreSayisi` onu saymaz (kendi temizleme yolu var) ama liste
   * onunla da daralır, dolayısıyla özet o durumda da anlamlıdır.
   */
  // ⚠ KAPI ile SÜZGEÇ AYNI GİRDİYİ okur: kapı anlık `q`ya baksaydı ilk harfe basıldığı an
  // açılır, süzgeç ise hâlâ gecikmeli BOŞ değeri taşırdı → özet bir tur TÜM siparişlerle
  // çalışır ve kartta kısa süre yanlış (süzgeçsiz) rakam görünürdü. Sunucu günlüğünde
  // `filterSummary?input={"0":{}}` olarak yakalandı (29 Ağu 2026).
  /**
   * MALİ ANALİZ düğmesi YALNIZ süper-admine gösterilir (maliyet/kâr + reçete kırılımı mahrem).
   * Gizlemek yetki DEĞİL — asıl kapı uçta (`order.costAnalysis` süper-admin arıyor); bu yalnız
   * kullanıcıyı yetkisi olmayan bir sayfaya göndermemek için.
   */
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const superAdmin = !!(meQ.data?.user as { isSuperAdmin?: boolean } | undefined)?.isSuperAdmin;

  const suzgecAktif = filtreSayisi > 0 || qGecikmeli.trim().length > 0;
  // Süzgeç kapalıyken uç HİÇ çağrılmaz: gösterilmeyecek kart için tüm siparişleri toplamak boşa iş.
  const ozetQ = trpc.order.filterSummary.useQuery(filtre, { enabled: suzgecAktif });
  const ozet = suzgecAktif ? (
    <SuzgecOzeti
      veri={ozetQ.data as unknown as OzetVeri | undefined}
      yukleniyor={ozetQ.isLoading}
      hata={ozetQ.error?.message}
      mobil={isMobile}
      turSuzgeci={turler.length > 0}
    />
  ) : null;

  /** Ters aralık sessizce 0 sonuç döndürürdü — söylüyoruz. */
  const aralikTers = !!dateFrom && !!dateTo && dateFrom > dateTo;

  const rows = (ordersQ.data?.rows ?? []) as unknown as OrderRow[];
  const total = ordersQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const drawers = (
    <>
      <OrderEditorDrawer open={editorOpen} orderId={editId} onClose={() => setEditorOpen(false)} />
      <OrderDetailDrawer
        open={!!detailId}
        orderId={detailId}
        onClose={() => setDetailId(null)}
        onEdit={(id) => { setDetailId(null); setEditId(id); setEditorOpen(true); }}
      />
    </>
  );

  /** Mobil: çekmece kapalıyken etkin süzgeçlerin tek satırlık özeti. */
  const ymdTr = (v: string) => v.split("-").reverse().join(".");
  const mobilSuzgecOzeti = [
    statuses.length ? `Durum: ${durumSecimOzeti(statuses)}` : null,
    turler.length ? `Tür: ${turSecimOzeti(turler, turListesi)}` : null,
    customerIds.length ? `${customerIds.length} müşteri` : null,
    dateFrom || dateTo ? `Teslim ${dateFrom ? ymdTr(dateFrom) : "…"} – ${dateTo ? ymdTr(dateTo) : "…"}` : null,
  ].filter(Boolean).join(" · ");

  // ═══ MOBİL GÖRÜNÜM (≤767px) — sipariş kartları ═══
  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          {/* Mobil dal AYRI render ediyor — buton buraya da eklenmezse özellik mobilde YOK olur. */}
          <div style={{ display: "flex", gap: 8, alignSelf: "flex-start" }}>
            <button type="button" className="btn-md primary sm" onClick={yeniSiparis}><Icon name="plus" size={14} />Yeni Sipariş</button>
            <button type="button" className="btn-md secondary sm" onClick={teslimatPlani}><Icon name="calendar" size={14} />Teslimat Planı</button>
            {/* Mobilde de SÖYLENİR: engellenen açılır pencere sessiz kalmamalı. */}
            {planHata ? <div className="form-banner-err" role="alert" style={{ marginTop: 8 }}>{planHata}</div> : null}
          </div>
          {/* Arama + SÜZGEÇ düğmesi. Mobilde süzgeç hiç yoktu (14 Eki 2026): durum ve ürün türü
              artık alttan açılan çekmecede; masaüstüyle AYNI state, AYNI sunucu girdisi. */}
          <div className="ord-msearch">
            <div className="m-search">
              <Icon name="search" size={15} />
              <input type="search" placeholder="Sipariş kodu, müşteri ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
            </div>
            <button
              type="button"
              className={`btn-md ${filtreSayisi ? "primary" : "secondary"}`}
              onClick={() => setFiltreAcik(true)}
              aria-label={filtreSayisi ? `Filtre — ${filtreSayisi} süzgeç etkin` : "Filtre"}
            >
              <Icon name="filter" size={14} />{filtreSayisi ? `Filtre (${filtreSayisi})` : "Filtre"}
            </button>
          </div>
          {/* Etkin süzgeç çekmece kapalıyken de GÖRÜNÜR: kullanıcı neden az sipariş gördüğünü bilsin. */}
          {mobilSuzgecOzeti ? <div className="ord-maktif">{mobilSuzgecOzeti}</div> : null}
        </div>

        {ozet}

        {ordersQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : ordersQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{ordersQ.error?.message}</div>
        ) : rows.length === 0 ? (
          // Süzgeç yüzünden boşsa "henüz sipariş yok" demek yanlış olur — kaldırma yolu gösterilir.
          <div className="m-empty">
            {q ? `"${q}" için sonuç yok.` : filtreSayisi > 0 ? "Seçili filtrelere uyan sipariş bulunamadı." : "Henüz sipariş yok."}
            {filtreSayisi > 0 ? (
              <button type="button" className="btn-md secondary ord-mtemizle" onClick={filtreleriTemizle}>
                <Icon name="x" size={14} />Filtreleri temizle
              </button>
            ) : null}
          </div>
        ) : (
          rows.map((o) => {
            return (
              <MCard pad key={o.id} className={o.gecikti ? "ord-gecikti-card" : ""} onClick={() => setDetailId(o.id)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{o.code}</span>
                  <span className={`badge ${durumRozetSinifiMobil(o.status)} no-dot`}>{durumEtiketi(o.status)}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--neutral-900)", marginTop: 6 }}>{o.customerName}</div>
                <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 1 }}>{o.orderType === "customer" ? "Müşteri siparişi" : "Stoğa üretim"}</div>
                {o.items.length ? (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
                    {o.items.slice(0, 4).map((it, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
                        <span style={{ color: "var(--neutral-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
                        <b className="mono" style={{ whiteSpace: "nowrap" }}>{fmtQty(it.qty)} {it.uom}</b>
                      </div>
                    ))}
                    {o.items.length > 4 ? <div style={{ fontSize: 11, color: "var(--neutral-400)" }}>+{o.items.length - 4} kalem daha</div> : null}
                  </div>
                ) : null}
                <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5 }}>
                  <span><span style={{ color: "var(--neutral-400)" }}>Sipariş </span><span className="mono" style={{ color: "var(--neutral-700)" }}>{fmtDate(o.createdAt)}</span></span>
                  <span><span style={{ color: "var(--neutral-400)" }}>Teslim </span><span className="mono" style={{ color: o.gecikti ? "var(--danger-text)" : "var(--neutral-700)", fontWeight: o.gecikti ? 700 : undefined }}>{o.teslimTarihi ? fmtDate(o.teslimTarihi) : "—"}</span>{o.teslimTahmini ? <span title="Söz verilen teslim tarihi girilmemiş — istenen tarih gösteriliyor" style={{ color: "var(--warning)", marginLeft: 3 }}>*</span> : null}</span>
                  {o.gecikti ? <span className="badge-pill b-danger ord-gec"><Icon name="alertTriangle" size={9} />Gecikti</span> : null}
                </div>
                {/* Mobil dal AYRI render ediyor: buraya eklenmezse özellik mobilde YOK olur.
                    Kartın kendi onClick'i detayı açıyor → bağlantıda yayılım durdurulur. */}
                {superAdmin ? (
                  <Link
                    to={`/orders/${o.id}/mali-analiz`}
                    onClick={(e) => e.stopPropagation()}
                    className="btn-md secondary sm"
                    style={{ marginTop: 8, textDecoration: "none", alignSelf: "flex-start" }}
                  >
                    <Icon name="banknote" size={13} />Mali Analiz
                  </Link>
                ) : null}
              </MCard>
            );
          })
        )}
        {totalPages > 1 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
            <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
            <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
            <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
          </div>
        ) : null}

        {/* MOBİL SÜZGEÇ ÇEKMECESİ — emsal Stok/Lotlar "Lot Filtresi". Seçim ANINDA uygulanır
            (liste arkada güncellenir); "Uygula" yalnız çekmeceyi kapatır. Dokunma hedefi 44 px. */}
        <Sheet open={filtreAcik} title="Sipariş Filtresi" onClose={() => setFiltreAcik(false)}
          footer={<>
            <button type="button" className="btn-md secondary" onClick={filtreleriTemizle} disabled={filtreSayisi === 0}>Temizle</button>
            <button type="button" className="btn-md primary" onClick={() => setFiltreAcik(false)}>Uygula</button>
          </>}>
          <div className="field" role="group" aria-labelledby="ord-m-durum">
            <span className="field-label" id="ord-m-durum">Durum</span>
            <div className="ord-mchips">
              <button
                type="button"
                className={`m-chip ord-mkisayol ${acikSecili ? "act" : ""}`}
                aria-pressed={acikSecili}
                onClick={acikKisayol}
              >
                <Icon name={acikSecili ? "check" : "filter"} size={14} />
                <span>{ACIK_SIPARISLER_AD} <span className="kume">({ACIK_SIPARISLER_KUME})</span></span>
              </button>
            </div>
            <div className="ord-mchips">
              {SIPARIS_DURUM_SIRASI.map((s) => {
                const sec = statuses.includes(s);
                return (
                  <button type="button" key={s} className={`m-chip ${sec ? "act" : ""}`} aria-pressed={sec} onClick={() => durumAcKapa(s)}>
                    {sec ? <Icon name="check" size={13} /> : null}{SIPARIS_DURUM_ETIKET[s]}
                  </button>
                );
              })}
            </div>
            <span className="ord-mipucu">Hiçbiri seçili değilse tüm durumlar listelenir.</span>
          </div>
          <div className="field" role="group" aria-labelledby="ord-m-tur">
            <span className="field-label" id="ord-m-tur">Ürün Türü</span>
            {typesQ.isLoading ? (
              <span className="ord-mipucu">Ürün türleri yükleniyor…</span>
            ) : typesQ.isError ? (
              <span className="ord-mipucu err" role="alert">Ürün türleri yüklenemedi: {typesQ.error.message}</span>
            ) : (
              <div className="ord-mchips">
                {turOptions.map((t) => {
                  const sec = turler.includes(t.value);
                  return (
                    <button type="button" key={t.value} className={`m-chip ${sec ? "act" : ""}`} aria-pressed={sec} onClick={() => turAcKapa(t.value)}>
                      {sec ? <Icon name="check" size={13} /> : null}{t.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Sheet>

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Siparişler</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Siparişler</h1>
          <p className="sub">Müşteri siparişleri + stoğa üretim · ATP · soft rezervasyon</p>
        </div>
        {/* Eylem butonu araç çubuğuna taşındı; burada yalnız (sekme kabuğunda görsel olarak
            gizlenen ama erişilebilirlik ağacında kalan) sayfa başlığı duruyor. */}
      </div>

      <div className="dt2-wrap">
        {/* `.ord-toolbar` = akışkan süzgeç davranışı (Orders.css). Sınıf Siparişler'e özel:
            diğer liste ekranları sabit genişlikli `.dt2-filter` düzenini korusun. */}
        <div className="dt2-toolbar ord-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Sipariş kodu, müşteri ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>

          {/* DURUM — çoklu seçim + "Açık siparişler" kısayolu (MRP seçicisiyle AYNI kalıp ve etiketler).
              Tetikleyici, seçim kısayola eşitse kısayolun adını, değilse kısa listeyi yazar. */}
          <div className="dt2-filter">
            <SearchSelect
              value={null}
              multiValues={statuses}
              onChangeMulti={durumSec}
              multiSummary={() => durumSecimOzeti(statuses)}
              options={DURUM_SECENEKLERI}
              onChange={() => {}}
              placeholder="Durum"
              searchPlaceholder="Durum ara…"
              showFooter={false}
              emptyText="—"
            />
          </div>
          <button
            type="button"
            className="btn-md secondary ord-kisayol"
            aria-pressed={acikSecili}
            onClick={acikKisayol}
            title={acikSecili ? "Kısayolu kaldır — tüm durumlar" : `Yalnız ${ACIK_SIPARISLER_KUME} durumundaki siparişler`}
          >
            <Icon name={acikSecili ? "check" : "filter"} size={13} />
            {ACIK_SIPARISLER_AD}
            <span className="ord-kisayol-kume">({ACIK_SIPARISLER_KUME})</span>
          </button>
          {/* ÜRÜN TÜRÜ (kart türü) — sipariş, en az bir satırı seçili türdeyse listelenir. */}
          <div className="dt2-filter">
            <SearchSelect
              value={null}
              multiValues={turler}
              onChangeMulti={turSec}
              multiSummary={() => turSecimOzeti(turler, turListesi)}
              options={turOptions}
              onChange={() => {}}
              placeholder={typesQ.isError ? "Ürün türü (yüklenemedi)" : "Ürün türü"}
              searchPlaceholder="Tür adı / kodu ara…"
              emptyText={turBosMetni}
              error={typesQ.isError}
            />
          </div>
          <div className="dt2-filter">
            <SearchSelect
              value={null}
              multiValues={customerIds}
              onChangeMulti={(v) => { setCustomerIds(v); setPage(1); }}
              multiSummary={(n) => `${n} müşteri`}
              options={customerOptions}
              onChange={() => {}}
              placeholder={customersQ.isError ? "Müşteri (yüklenemedi)" : "Müşteri"}
              searchPlaceholder={musteriEksik ? "İlk 100 müşteri — dahası için üstteki aramayı kullanın" : "Müşteri adı / kodu ara…"}
              emptyText={musteriBosMetni}
              error={customersQ.isError}
            />
          </div>

          {/* TESLİM aralığı — hazır aralık + iki uç. Hepsi tek grupta duruyor ki
              "hangi tarih" sorusu ("sipariş mi teslim mi") etiketle cevaplansın. */}
          <div className="ord-range">
            <span className="ord-range-lbl">Teslim</span>
            <div className="dt2-filter sm">
              <SearchSelect
                value={hizli || null}
                options={hizliOptions}
                onChange={(v) => hizliSec((v as HizliAralik) ?? null)}
                placeholder="Hızlı aralık"
                showFooter={false}
                emptyText="—"
              />
            </div>
            <DateField value={dateFrom} onChange={(v) => tarihElle("from", v)} placeholder="başlangıç" aria-label="Teslim başlangıç tarihi" />
            <span className="ord-range-sep" aria-hidden="true">–</span>
            <DateField value={dateTo} onChange={(v) => tarihElle("to", v)} min={dateFrom || undefined} placeholder="bitiş" aria-label="Teslim bitiş tarihi" />
          </div>

          {filtreSayisi > 0 ? (
            <button type="button" className="btn" onClick={filtreleriTemizle} title="Tüm filtreleri kaldır">
              <Icon name="x" size={13} />Filtreleri temizle ({filtreSayisi})
            </button>
          ) : null}

          <span className="grow" />
          {planHata ? <span style={{ fontSize: 11.5, color: "var(--danger)", maxWidth: 260 }}>{planHata}</span> : null}
          <button type="button" className="btn" onClick={teslimatPlani} title="Süzgeçlenmiş siparişleri teslim tarihine göre gün gün A4 olarak yazdır">
            <Icon name="calendar" size={13} />Teslimat Planı
          </button>
          {/* AYNA (ZAMANSAL): Yenile ekrandaki HER İKİ cevabı da tazeler. Yalnız liste
              tazelenseydi — `staleTime` 30 sn ve pencere odağında yeniden getirme kapalı —
              KPI kartları bayat kalır, aynı soruya iki farklı rakam görünürdü. */}
          <button
            type="button" className="btn"
            onClick={() => { void ordersQ.refetch(); if (suzgecAktif) void ozetQ.refetch(); }}
            disabled={ordersQ.isFetching || ozetQ.isFetching}
          >
            {ordersQ.isFetching || ozetQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
          {/* "+ Yeni Sipariş" başlık şeridinden BURAYA taşındı: sekme kabuğu sayfa başlığını
              gizlediği için o şerit yalnız bu buton için ayakta duruyordu; eylem, üzerinde
              işlem yapılan tablonun kendi araç çubuğuna ait. */}
          <button type="button" className="btn-md primary sm" onClick={yeniSiparis}>
            <Icon name="plus" size={14} />Yeni Sipariş
          </button>
        </div>

        {aralikTers ? (
          <div className="ord-warn">
            <Icon name="alertTriangle" size={14} />
            Başlangıç tarihi bitişten sonra — bu aralık hiçbir siparişi kapsamaz.
          </div>
        ) : null}

        {ozet}

        <div className="dt2-scroll">
          {ordersQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : ordersQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{ordersQ.error?.message}</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              {/* Boş sonuç ile boş VERİ farklı: süzgeç yüzünden boşsa kullanıcıya
                  "sipariş yok" demek yanlış olur — süzgeci kaldırma yolunu gösteririz. */}
              <p className="ttl">{q || filtreSayisi > 0 ? "Sonuç yok" : "Henüz sipariş yok"}</p>
              <p className="sub">
                {q ? `"${q}" için sonuç yok.` : filtreSayisi > 0 ? "Seçili filtrelere uyan sipariş bulunamadı." : "İlk siparişi oluşturun."}
              </p>
              {filtreSayisi > 0 ? (
                <button type="button" className="btn-md secondary" style={{ marginTop: 12 }} onClick={filtreleriTemizle}>
                  <Icon name="x" size={14} />Filtreleri temizle
                </button>
              ) : !q ? (
                <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={yeniSiparis}><Icon name="plus" size={14} />Yeni Sipariş</button>
              ) : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Sipariş</th>
                  <th>Müşteri</th>
                  <th style={{ width: 76 }}>Tip</th>
                  <th style={{ width: 230 }}>Kalemler</th>
                  <th style={{ width: 150 }}>Tarihler</th>
                  <th style={{ width: 100 }}>Durum</th>
                  <th style={{ width: 80 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  return (
                    // Gecikmiş sipariş KOYU KIRMIZI çerçevelenir (kullanıcı isteği, 29 Ağu 2026).
                    // Renk TEK BAŞINA yetmez → "Teslim" hücresine ayrıca rozet basılır.
                    <tr key={o.id} className={o.gecikti ? "ord-gecikti" : undefined}>
                      <td><span className="cell-id">{o.code}</span></td>
                      <td>{o.customerName}</td>
                      <td style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{o.orderType === "customer" ? "Müşteri" : "Stoğa"}</td>
                      <td>
                        {o.items.length === 0 ? (
                          <span style={{ color: "var(--neutral-400)" }}>—</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {o.items.map((it, i) => (
                              <span key={i} style={{ fontSize: 11.5, display: "flex", justifyContent: "space-between", gap: 10, lineHeight: 1.35 }} title={it.code}>
                                <span style={{ color: "var(--neutral-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</span>
                                <b className="mono" style={{ whiteSpace: "nowrap" }}>{fmtQty(it.qty)} {it.uom}</b>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 11.5 }}>
                          <span><span style={{ color: "var(--neutral-400)" }}>Sipariş: </span><span className="mono" style={{ color: "var(--neutral-700)" }}>{fmtDate(o.createdAt)}</span></span>
                          <span><span style={{ color: "var(--neutral-400)" }}>Teslim: </span><span className="mono" style={{ color: o.gecikti ? "var(--danger-text)" : "var(--neutral-700)", fontWeight: o.gecikti ? 700 : undefined }}>{o.teslimTarihi ? fmtDate(o.teslimTarihi) : "—"}</span>{o.teslimTahmini ? <span title="Söz verilen teslim tarihi girilmemiş — istenen tarih gösteriliyor" style={{ color: "var(--warning)", marginLeft: 3 }}>*</span> : null}</span>
                          {o.gecikti ? <span className="badge-pill b-danger ord-gec"><Icon name="alertTriangle" size={9} />Gecikti</span> : null}
                        </div>
                      </td>
                      <td><span className={`badge-pill ${durumRozetSinifi(o.status)}`}>{durumEtiketi(o.status)}</span></td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          {superAdmin ? (
                            <Link
                              to={`/orders/${o.id}/mali-analiz`}
                              className="btn-md secondary"
                              style={{ height: 30, padding: "0 10px", fontSize: 12.5, textDecoration: "none", whiteSpace: "nowrap" }}
                              title="Maliyet ve kâr analizi — reçete kalemleri, grup kırılımı"
                            >
                              <Icon name="banknote" size={13} />Mali Analiz
                            </Link>
                          ) : null}
                          <button type="button" className="btn-md secondary" style={{ height: 30, padding: "0 14px", fontSize: 12.5 }} onClick={() => setDetailId(o.id)}>Detay</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> sipariş · <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}
